import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { SnAlarm, SnCamera, SnCameraDrone, SnMapView } from '@lib/api/entities';
import { FsMapDockedViewServiceProvider, IFsMap, MapViewData } from '@lib/geo/core';
import { FsLvtCamera } from '@lib/lvt';

import { FsMap } from '../types';

@Injectable()
export class MapDockedViewFacadeService<
  TMap extends IFsMap,
> extends FsMapDockedViewServiceProvider<TMap> {
  constructor(private readonly provider: FsMapDockedViewServiceProvider<TMap>) {
    super();
  }

  public get telestrationView(): boolean {
    return this.provider.telestrationView;
  }

  public get telestrationMode(): string {
    return this.provider.telestrationMode;
  }

  public get QUERY_PARAM_DOCKED_VIEW(): string {
    return this.provider.QUERY_PARAM_DOCKED_VIEW;
  }

  public get view$(): Observable<SnMapView> {
    return this.provider.view$;
  }

  public get views$(): Observable<SnMapView[]> {
    return this.provider.views$;
  }

  public get removeMarker$(): Observable<any> {
    return this.provider.removeMarker$;
  }

  public get cameras$(): Observable<SnCamera[]> {
    return this.provider.cameras$;
  }

  public get drones$(): Observable<SnCameraDrone[]> {
    return this.provider.drones$;
  }

  public get playbackCameras$(): Observable<SnCamera[]> {
    return this.provider.playbackCameras$;
  }

  public get cameraExistsAlready$(): Observable<SnCamera> {
    return this.provider.cameraExistsAlready$;
  }

  public get initPlayback$(): Observable<SnMapView> {
    return this.provider.initPlayback$;
  }

  public get removedTab$(): Observable<SnMapView> {
    return this.provider.removedTab$;
  }

  public get switched$(): Observable<void> {
    return this.provider.switched$;
  }

  public get mapUpdated$(): Observable<any> {
    return this.provider.mapUpdated$;
  }

  public get updateTab$(): Observable<SnMapView> {
    return this.provider.updateTab$;
  }

  public get featureMouseOver$(): Observable<any> {
    return this.provider.featureMouseOver$;
  }

  public get featureMouseOut$(): Observable<any> {
    return this.provider.featureMouseOut$;
  }

  public get skydioUpdated$(): Observable<any> {
    return this.provider.skydioUpdated$;
  }

  public get coreMobileUpdated$(): Observable<any> {
    return this.provider.coreMobileUpdated$;
  }

  public get geospatialProvider(): string {
    return this.provider.geospatialProvider;
  }

  public get id(): string {
    return this.provider.id;
  }

  public get map(): TMap {
    return this.provider.map;
  }

  public get currentView(): SnMapView {
    return this.provider.currentView;
  }

  public get currentViewIndex(): number {
    return this.provider.currentViewIndex;
  }

  public get cameras(): SnCamera[] {
    return this.provider.cameras;
  }

  public get drones(): SnCameraDrone[] {
    return this.provider.drones;
  }

  public get lvts(): FsLvtCamera[] {
    return this.provider.lvts;
  }

  public get playbackCams(): SnCamera[] {
    return this.provider.playbackCams;
  }

  public get views(): SnMapView[] {
    return this.provider.views;
  }

  public get loaded(): boolean {
    return this.provider.loaded;
  }

  public setClosedAlarm(alarm: SnAlarm): void {
    this.provider.setClosedAlarm(alarm);
  }

  public getClosedAlarm(): Observable<SnAlarm> {
    return this.provider.getClosedAlarm();
  }

  public onMapLoaded(map: TMap): void {
    this.provider.onMapLoaded(map);
  }

  public createViewForNearbyCameras(data: MapViewData, radius: number): void {
    this.provider.createViewForNearbyCameras(data, radius);
  }

  public createViewForCameras(data: MapViewData, cameras: SnCamera[], zoomLevel: number): void {
    this.provider.createViewForCameras(data, cameras, zoomLevel);
  }

  public featureMouseOver(feature: any): void {
    this.provider.featureMouseOver(feature);
  }

  public featureMouseOut(feature: any): void {
    this.provider.featureMouseOut(feature);
  }

  public skydioUpdate(feature: any): void {
    this.provider.skydioUpdate(feature);
  }

  public coreMobileUpdate(feature: any): void {
    this.provider.coreMobileUpdate(feature);
  }

  public setView(mapView?: SnMapView, controls?: boolean): void {
    this.provider.setView(mapView, controls);
  }

  public setViewByCitizenIncidentId(citizenIncidentId: string): Observable<SnMapView | undefined> {
    return this.provider.setViewByCitizenIncidentId(citizenIncidentId);
  }

  public backToMap(): void {
    this.provider.backToMap();
  }

  public goToSavedMap(): void {
    this.provider.goToSavedMap();
  }

  public setViews(mapViews: SnMapView[]): void {
    this.provider.setViews(mapViews);
  }

  public updateTab(view: SnMapView): void {
    this.provider.updateTab(view);
  }

  public addCamera(camera: SnCamera): void {
    this.provider.addCamera(camera);
  }

  public addDrone(drone: SnCameraDrone): void {
    this.provider.addDrone(drone);
  }

  public dockCameras(cameras: SnCamera[]): void {
    this.provider.dockCameras(cameras);
  }

  public dockDrone(drone: SnCameraDrone[]): void {
    this.provider.dockDrone(drone);
  }

  public removeCamera(camera: SnCamera): void {
    this.provider.removeCamera(camera);
  }

  public removeDrone(drone: SnCameraDrone): void {
    this.provider.removeDrone(drone);
  }

  public removeAllCameras(): void {
    this.provider.removeAllCameras();
  }

  public viewPlayback(camera: SnCamera): void {
    this.provider.viewPlayback(camera);
  }

  public removePlayback(camera: SnCamera): void {
    this.provider.removePlayback(camera);
  }

  public updateView(
    lon: number,
    lat: number,
    zoomLevel: number,
    metaData?: any,
  ): Observable<SnMapView> {
    return this.provider.updateView(lon, lat, zoomLevel, metaData);
  }

  public deleteView(
    view: SnMapView,
    options?: {
      message?: string;
      archiveButtonText?: string;
      deleteButtonText?: string;
      successMessage?: string;
      errorMessage?: string;
      disableAltBtn?: boolean;
    },
  ): Observable<any> {
    return this.provider.deleteView(view, options);
  }

  public deleteViews(view: SnMapView): Observable<any> {
    return this.provider.deleteViews(view);
  }

  public archiveViews(view: SnMapView): Observable<any> {
    return this.provider.archiveViews(view);
  }

  public updateMapView(): void {
    this.provider.updateMapView();
  }

  public silentUpdate(): Observable<SnMapView> {
    return this.provider.silentUpdate();
  }

  public removeTab(tab: SnMapView): void {
    this.provider.removeTab(tab);
  }

  public setTabNumber(tabAmount: number): void {
    this.provider.setTabNumber(tabAmount);
  }

  public dockModemCameras(feature: any): void {
    this.provider.dockModemCameras(feature);
  }

  public createModemGrid(applianceIds: string[]): void {
    this.provider.createModemGrid(applianceIds);
  }

  public clearViewCameras(): void {
    this.provider.clearViewCameras();
  }

  public setCurrentViewIndex(index: number): void {
    this.provider.setCurrentViewIndex(index);
  }
}
